import React from "react";
import { Reveal, Overline } from "./Motion";
import { SmartImage } from "./SmartImage";
import { VERIFIED } from "@/data/cafe";
import { useDialogs } from "./DialogProvider";
import { Briefcase, Laptop, Video, Wifi, Check } from "lucide-react";

const cards = [
  { icon: Briefcase, title: "Business Meetings", desc: "Private, quiet corners with fast Wi-Fi and fresh coffee on tap — perfect for client meets and team huddles." },
  { icon: Laptop, title: "Work & Co-work", desc: "Comfortable seating, power sockets and unlimited refills for a productive work-from-café day." },
  { icon: Video, title: "Video & Photo Shoots", desc: "Book our space for content, reels and product shoots — a clean white backdrop is available on request." },
];

const perks = ["Free high-speed Wi-Fi", "Clean white backdrop for shoots", "Power sockets at every table", "Catering & refreshments on request"];

export default function Spaces() {
  const { open } = useDialogs();
  return (
    <section id="spaces" className="bg-white py-24 md:py-32" data-testid="spaces-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <Overline>Meetings · Work · Shoots</Overline>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1514] mt-4 font-medium leading-[1.05]">
              Your space to <span className="italic text-[#1C7C54]">meet, work & shoot</span>
            </h2>
            <p className="font-body text-[#1A1514]/65 text-lg leading-relaxed mt-6 max-w-lg">
              Teaavi Coffee isn't just for coffee. Host your business meetings, settle in for a focused work day,
              or book the space for your video and photo shoots — complete with a clean white backdrop.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              {perks.map((p) => (
                <div key={p} className="flex items-center gap-3" data-testid={`space-perk-${p.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <span className="h-6 w-6 grid place-items-center rounded-full bg-[#1C7C54]/12 shrink-0">
                    <Check className="h-3.5 w-3.5 text-[#1C7C54]" />
                  </span>
                  <span className="font-body text-sm text-[#1A1514]/75">{p}</span>
                </div>
              ))}
            </div>

            <button onClick={() => open("event", { eventType: "Video / Photo Shoot" })} data-testid="spaces-book-btn"
              className="mt-9 rounded-sm bg-[#1C7C54] text-white px-7 py-3.5 font-accent text-xs uppercase tracking-[0.15em] hover:bg-[#14603F] transition-colors">
              Book the Space
            </button>
          </div>

          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:row-span-2 overflow-hidden rounded-sm group">
              <SmartImage src={VERIFIED.interior1} alt="Café workspace" className="h-full w-full min-h-[300px] object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            {cards.slice(0, 3).map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="border border-[#1A1514]/12 rounded-sm p-6 h-full bg-white hover:border-[#1C7C54] transition-colors" data-testid={`space-card-${i}`}>
                  <c.icon className="h-6 w-6 text-[#1C7C54]" strokeWidth={1.5} />
                  <h3 className="font-display text-xl text-[#1A1514] mt-4">{c.title}</h3>
                  <p className="font-body text-sm text-[#1A1514]/60 mt-2 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
